import type {
  ChatThreadEvent,
  ChatThreadSnapshotProjection,
} from "@vm0/api-contracts/contracts/chat-threads";

export type ReplayChatThreadEvent = Omit<ChatThreadEvent, "seqId">;

export interface EventDrivenChatThread extends ChatThreadSnapshotProjection {
  readonly sortAt: string;
  readonly cloudBrowserEnabled: boolean;
}

function compareThreadOrder(
  left: EventDrivenChatThread,
  right: EventDrivenChatThread,
): number {
  const leftPinned = left.pinnedAt !== null;
  const rightPinned = right.pinnedAt !== null;
  if (leftPinned !== rightPinned) {
    return leftPinned ? -1 : 1;
  }
  const sortCompare = right.sortAt.localeCompare(left.sortAt);
  if (sortCompare !== 0) {
    return sortCompare;
  }
  return right.id.localeCompare(left.id);
}

function applyEvent(
  threads: Map<string, EventDrivenChatThread>,
  event: ReplayChatThreadEvent,
  pendingThreadUpdates: Map<string, ReplayChatThreadEvent[]>,
) {
  if (event.kind === "created") {
    threads.set(event.chatThreadId, {
      id: event.chatThreadId,
      agentId: event.agentId,
      title: event.title,
      sortAt: event.createdAt,
      createdAt: event.createdAt,
      updatedAt: event.createdAt,
      pinnedAt: null,
      renamedAt: null,
      selectedModel: event.selectedModel,
      serviceTier: event.serviceTier,
      computerUseHostId: event.computerUseHostId,
      cloudBrowserEnabled: event.cloudBrowserEnabled ?? false,
    });
    const pendingUpdates = pendingThreadUpdates.get(event.chatThreadId) ?? [];
    pendingThreadUpdates.delete(event.chatThreadId);
    for (const pendingUpdate of pendingUpdates) {
      if (pendingUpdate.createdAt.localeCompare(event.createdAt) >= 0) {
        applyEvent(threads, pendingUpdate, pendingThreadUpdates);
      }
    }
    return;
  }

  if (event.kind === "deleted") {
    threads.delete(event.chatThreadId);
    return;
  }

  const thread = threads.get(event.chatThreadId);
  if (!thread) {
    if (
      event.kind === "model_selection_updated" ||
      event.kind === "service_tier_updated" ||
      event.kind === "computer_use_host_updated"
    ) {
      const pendingUpdates = pendingThreadUpdates.get(event.chatThreadId) ?? [];
      pendingThreadUpdates.set(event.chatThreadId, [...pendingUpdates, event]);
    }
    return;
  }

  if (event.kind === "renamed") {
    threads.set(event.chatThreadId, {
      ...thread,
      title: event.title,
      renamedAt: event.createdAt,
      updatedAt: event.createdAt,
    });
    return;
  }

  if (event.kind === "pinned") {
    threads.set(event.chatThreadId, {
      ...thread,
      pinnedAt: event.createdAt,
      updatedAt: event.createdAt,
    });
    return;
  }

  if (event.kind === "unpinned") {
    threads.set(event.chatThreadId, {
      ...thread,
      pinnedAt: null,
      updatedAt: event.createdAt,
    });
    return;
  }

  if (event.kind === "model_selection_updated") {
    threads.set(event.chatThreadId, {
      ...thread,
      selectedModel: event.selectedModel,
      updatedAt: event.createdAt,
    });
    return;
  }

  if (event.kind === "service_tier_updated") {
    threads.set(event.chatThreadId, {
      ...thread,
      serviceTier: event.serviceTier,
      updatedAt: event.createdAt,
    });
    return;
  }

  if (event.kind === "computer_use_host_updated") {
    threads.set(event.chatThreadId, {
      ...thread,
      computerUseHostId: event.computerUseHostId,
      cloudBrowserEnabled: event.cloudBrowserEnabled ?? false,
      updatedAt: event.createdAt,
    });
    return;
  }

  threads.set(event.chatThreadId, {
    ...thread,
    sortAt: event.createdAt,
  });
}

export function replayChatThreadEvents(
  snapshot: readonly ChatThreadSnapshotProjection[],
  events: readonly ReplayChatThreadEvent[],
): EventDrivenChatThread[] {
  const threads = new Map<string, EventDrivenChatThread>();
  for (const thread of snapshot) {
    threads.set(thread.id, {
      ...thread,
      selectedModel: thread.selectedModel ?? null,
      serviceTier: thread.serviceTier ?? null,
      computerUseHostId: thread.computerUseHostId ?? null,
      cloudBrowserEnabled: thread.cloudBrowserEnabled ?? false,
    });
  }
  const pendingThreadUpdates = new Map<string, ReplayChatThreadEvent[]>();
  for (const event of events) {
    applyEvent(threads, event, pendingThreadUpdates);
  }
  return [...threads.values()].sort(compareThreadOrder);
}
